import { toArgsArray, toArgsString } from 'app/model/adapters/activities/arguments-adapter.util';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { PigActivity } from 'app/model/pipeline/pig.activity';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { HDInsightLinkedService } from 'app/model/linkedservice/hdinsight.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { ValueAdapter } from './../value.adapter';
import { FilePath } from 'app/model/dataset/file-path';
import _ from 'lodash';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(PigActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            scriptPath: '*',
            getDebugInfo: '*',
            arguments: '*',
            storageLinkedServices: linkedServiceReferenceWithoutParameterizationRemainer,
            scriptLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            defines: '*'
        }
    }
})
export class HDInsightPigActivityAdapter extends ActivityAsyncAdapter<PigActivity, Model.HDInsightPigActivity> {

    public model(): IModel {
        return Metamodel.modelFor(PigActivity);
    }

    protected async _toResource(activity: PigActivity): Promise<Model.HDInsightPigActivity> {
        const modelHDInsightPigActivity: Model.HDInsightPigActivity = super._getCommonActivityProperties(activity, 'HDInsightPig') as Model.HDInsightPigActivity;
        const pigActivity: PigActivity = activity as PigActivity;

        const scriptPath = ValueAdapter.toResource(pigActivity, PigActivity.SCRIPT_PATH);

        modelHDInsightPigActivity.typeProperties = {
            scriptPath: scriptPath.type ? scriptPath : scriptPath.path,
            arguments: toArgsArray(pigActivity.pigActivityArguments, pigActivity, PigActivity.ARGUMENTS),
            getDebugInfo: pigActivity.getDebugInfo,
            defines: toParameterObject(pigActivity.defines)
        };

        if (pigActivity.scriptLinkedService) {
            modelHDInsightPigActivity.typeProperties.scriptLinkedService = this._toLinkedServiceReferenceModel(pigActivity.scriptLinkedService);
        }

        if (pigActivity.linkedService) {
            modelHDInsightPigActivity.linkedServiceName = this._toLinkedServiceReferenceModel(pigActivity.linkedService);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on hive activity
            _.merge(modelHDInsightPigActivity, parsed);
        }

        return modelHDInsightPigActivity;
    }

    protected async _toModel(modelActivity: Model.HDInsightPigActivity): Promise<PigActivity> {
        const activity: PigActivity = PigActivity.MODEL.create<PigActivity>(modelActivity.name);
        const modelPigActivity = modelActivity as Model.HDInsightPigActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (modelPigActivity.typeProperties) {
            if (modelPigActivity.typeProperties.scriptPath && modelPigActivity.typeProperties.scriptPath.type) {
                ValueAdapter.toModel(activity, PigActivity.SCRIPT_PATH, modelPigActivity.typeProperties.scriptPath);
            } else {
                activity.scriptPath = new FilePath();
                activity.scriptPath.path = modelPigActivity.typeProperties.scriptPath || '';
            }

            const args = toArgsString(activity, modelPigActivity.typeProperties.arguments, PigActivity.ARGUMENTS);
            if (args) {
                activity.pigActivityArguments = args;
            }
            activity.getDebugInfo = modelPigActivity.typeProperties.getDebugInfo;
            activity.defines = toParameterClass(modelPigActivity.typeProperties.defines, activity);
            if (modelPigActivity.typeProperties.scriptLinkedService !== undefined) {
                activity.scriptLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                    modelPigActivity.typeProperties.scriptLinkedService, this.context);
            }
        }

        if (modelPigActivity.linkedServiceName !== undefined) {
            activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(HDInsightLinkedService),
                modelPigActivity.linkedServiceName, this.context);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelPigActivity, PigActivity.MODEL));

        return activity;
    }
}
